import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * API para gestión de Extras de Pagos
 * Maneja la comunicación con el backend para extras y tipos de extras
 */

/**
 * Obtener todos los extras con filtros opcionales
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - Parámetros de filtrado { tipo_extra_id, extra }
 * @returns {Promise} Response con lista de extras
 */
const getExtras = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    
    // Construir query string desde los parámetros
    const searchParams = new URLSearchParams();
    
    if (params.tipo_extra_id) {
      searchParams.append('tipo_extra_id', params.tipo_extra_id);
    }
    if (params.extra) {
      searchParams.append('extra', params.extra);
    }

    // Construir URL evitando doble slash
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/extras/';
    if (searchParams.toString()) {
      endpoint += '?' + searchParams.toString();
    }

    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0,
        message: response.data.message || 'Extras obtenidos exitosamente'
      };
    }
    
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      total: 0
    };
  } catch (error) {
    console.error('Error al obtener extras:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener extras',
      data: [],
      total: 0
    };
  }
};

/**
 * Obtener todos los tipos de extra
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Response con lista de tipos de extra
 */
const getTiposExtra = async (user) => {
  try {
    const config = basicAuthConfig(user);
    
    // Construir URL evitando doble slash
    const endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/extras/tipo_extra';
    
    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0,
        message: 'Tipos de extra obtenidos exitosamente'
      };
    }
    
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      total: 0
    };
  } catch (error) {
    console.error('Error al obtener tipos de extra:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener tipos de extra',
      data: [],
      total: 0
    };
  }
};

/**
 * Crear un nuevo extra
 * @param {Object} user - Usuario autenticado
 * @param {Object} extraData - Datos del extra { extra, monto, tipo_extra_id }
 * @returns {Promise} Response con resultado de la operación
 */
const createExtra = async (user, extraData) => {
  try {
    // Validar datos antes de enviar
    const validationResult = validateExtraData(extraData);
    if (!validationResult.isValid) {
      return {
        success: false,
        error: validationResult.error
      };
    }

    const config = basicAuthConfig(user);
    
    // Construir URL evitando doble slash
    const endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/extras/';
    
    const response = await axios.post(endpoint, extraData, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || 'Extra creado exitosamente'
      };
    }
    
    return {
      success: false,
      error: 'No se recibió confirmación del servidor'
    };
  } catch (error) {
    console.error('Error al crear extra:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear extra'
    };
  }
};

/**
 * Actualizar un extra existente
 * @param {Object} user - Usuario autenticado
 * @param {number} id - ID del extra a actualizar
 * @param {Object} extraData - Datos del extra { extra, monto, tipo_extra_id }
 * @returns {Promise} Response con resultado de la operación
 */
const updateExtra = async (user, id, extraData) => {
  try {
    // Validar datos antes de enviar
    const validationResult = validateExtraData(extraData);
    if (!validationResult.isValid) {
      return {
        success: false,
        error: validationResult.error
      };
    }

    const config = basicAuthConfig(user);
    
    // Construir URL evitando doble slash
    const endpoint = urlMyspreadApi.replace(/\/$/, '') + `/mantenedores/extras/${id}`;
    
    const response = await axios.put(endpoint, extraData, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || 'Extra actualizado exitosamente'
      };
    }
    
    return {
      success: false,
      error: 'No se recibió confirmación del servidor'
    };
  } catch (error) {
    console.error('Error al actualizar extra:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar extra'
    };
  }
};

/**
 * Eliminar un extra
 * @param {Object} user - Usuario autenticado
 * @param {number} id - ID del extra a eliminar
 * @returns {Promise} Response con resultado de la operación
 */
const deleteExtra = async (user, id) => {
  try {
    const config = basicAuthConfig(user);
    
    // Construir URL evitando doble slash
    const endpoint = urlMyspreadApi.replace(/\/$/, '') + `/mantenedores/extras/${id}`;
    
    const response = await axios.delete(endpoint, config);
    
    return {
      success: true,
      message: response.data?.message || 'Extra eliminado exitosamente'
    };
  } catch (error) {
    console.error('Error al eliminar extra:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al eliminar extra'
    };
  }
};

/**
 * Validar datos de extra antes de enviar al servidor
 * @param {Object} extraData - Datos del extra
 * @returns {Object} { isValid: boolean, error: string }
 */
const validateExtraData = (extraData) => {
  if (!extraData) {
    return { isValid: false, error: 'Datos del extra son requeridos' };
  }

  if (!extraData.extra || extraData.extra.trim() === '') {
    return { isValid: false, error: 'El nombre del extra es requerido' };
  }

  if (!extraData.monto || extraData.monto <= 0) {
    return { isValid: false, error: 'El monto debe ser mayor a 0' };
  }

  if (!extraData.tipo_extra_id) {
    return { isValid: false, error: 'El tipo de extra es requerido' };
  }

  // Validar que el monto sea un número entero
  if (!Number.isInteger(Number(extraData.monto))) {
    return { isValid: false, error: 'El monto debe ser un número entero sin decimales' };
  }

  return { isValid: true };
};

/**
 * Obtener todas las asignaciones de extras con filtros opcionales
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - Parámetros de filtrado { pageSize, pageNumber, searchValue }
 * @returns {Promise} Response con lista de asignaciones
 */
const getAsignaciones = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    
    // Construir query string desde los parámetros
    const searchParams = new URLSearchParams();
    
    if (params.pageSize) {
      searchParams.append('pageSize', params.pageSize);
    }
    if (params.pageNumber) {
      searchParams.append('pageNumber', params.pageNumber);
    }
    if (params.searchValue) {
      searchParams.append('searchValue', params.searchValue);
    }
    if (params.usuario_movil_id) {
      searchParams.append('usuario_movil_id', params.usuario_movil_id);
    }
    if (params.tipo_extra_id) {
      searchParams.append('tipo_extra_id', params.tipo_extra_id);
    }
    if (params.extra_id) {
      searchParams.append('extra_id', params.extra_id);
    }
    if (params.empresa_id) {
      searchParams.append('empresa_id', params.empresa_id);
    }
    if (params.estado_id) {
      searchParams.append('estado_id', params.estado_id);
    }
    if (params.fecha_inicio) {
      searchParams.append('fecha_inicio', params.fecha_inicio);
    }
    if (params.fecha_fin) {
      searchParams.append('fecha_fin', params.fecha_fin);
    }

    // Construir URL
    const baseUrl = urlMyspreadApi.replace(/\/$/, '');
    const queryString = searchParams.toString();
    const url = queryString ? `${baseUrl}/mantenedores/extras/asignaciones?${queryString}` : `${baseUrl}/mantenedores/extras/asignaciones`;

    const response = await axios.get(url, config);
    
    return {
      success: true,
      data: response.data.data || [],
      total: response.data.total || 0,
      message: response.data.message || 'Asignaciones obtenidas exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener asignaciones:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener asignaciones',
      data: [],
      total: 0
    };
  }
};

/**
 * Crear nueva asignación de extra
 * @param {Object} user - Usuario autenticado
 * @param {Object} asignacionData - Datos de la asignación
 * @returns {Promise} Response de la creación
 */
const createAsignacion = async (user, asignacionData) => {
  try {
    const config = basicAuthConfig(user);
    const url = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/extras/asignaciones`;

    const response = await axios.post(url, asignacionData, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Asignación creada exitosamente'
    };
  } catch (error) {
    console.error('Error al crear asignación:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear asignación'
    };
  }
};

/**
 * Actualizar una asignación existente
 * @param {Object} user - Usuario autenticado
 * @param {number} asignacionId - ID de la asignación a actualizar
 * @param {Object} data - Datos de actualización
 * @returns {Promise} Response de la actualización
 */
const updateAsignacion = async (user, asignacionId, data) => {
  try {
    const config = basicAuthConfig(user);
    const url = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/extras/asignaciones/${asignacionId}`;

    const response = await axios.put(url, data, config);

    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Asignación actualizada exitosamente'
    };
  } catch (error) {
    console.error('Error al actualizar asignación:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar asignación'
    };
  }
};


/**
 * Actualizar estado de múltiples asignaciones de forma masiva
 * @param {Object} user - Usuario autenticado
 * @param {Object} data - Datos de actualización { asignacion_ids: [], estado: string }
 * @returns {Promise} Response de la actualización masiva
 */
const bulkUpdateAsignacionesStatus = async (user, data) => {
  try {
    const config = basicAuthConfig(user);
    const url = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/extras/asignaciones/bulk-update-status`;

    const response = await axios.put(url, data, config);

    return {
      success: true,
      data: response.data.data || {},
      summary: response.data.summary || {},
      message: response.data.message || 'Estados actualizados exitosamente'
    };
  } catch (error) {
    console.error('Error al actualizar estados masivamente:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar estados masivamente'
    };
  }
};

/**
 * Descargar plantilla Excel para carga masiva de asignaciones de extras
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Response con resultado de la descarga
 */
const downloadPlantillaAsignacionesBulk = async (user) => {
  try {
    const config = {
      ...basicAuthConfig(user),
      responseType: 'blob'
    };

    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/extras/asignaciones/plantilla-bulk`;
    const response = await axios.get(endpoint, config);

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'plantilla_asignaciones_extras_bulk.xlsx';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      message: 'Plantilla descargada exitosamente'
    };
  } catch (error) {
    console.error('Error al descargar plantilla de asignaciones:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al descargar plantilla'
    };
  }
};

/**
 * Validar archivo Excel para carga masiva de asignaciones de extras
 * @param {Object} user - Usuario autenticado
 * @param {File} file - Archivo Excel
 * @returns {Promise} Response con resultado de validación
 */
const validateAsignacionesBulk = async (user, file) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/extras/asignaciones/bulk/validate`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(endpoint, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data'
      }
    });

    return {
      success: response.data?.success ?? true,
      data: response.data,
      message: response.data?.message || 'Archivo validado exitosamente'
    };
  } catch (error) {
    const errorData = error.response?.data || {};
    return {
      success: false,
      error: errorData.message || errorData.error || 'Error al validar archivo',
      errores: errorData.errores || [],
      total_errores: errorData.total_errores || 0,
      fase: errorData.fase || null,
      data: errorData
    };
  }
};

/**
 * Subir archivo Excel para crear asignaciones de extras en proceso asíncrono
 * @param {Object} user - Usuario autenticado
 * @param {File} file - Archivo Excel
 * @returns {Promise} Response con processId
 */
const uploadAsignacionesBulk = async (user, file) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/extras/asignaciones/bulk/create`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(endpoint, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response.status === 202 && response.data) {
      return {
        success: response.data.success || true,
        processId: response.data.process_id,
        totalFilas: response.data.total_filas,
        message: response.data.message,
        statusUrl: response.data.status_url
      };
    }

    return {
      success: true,
      message: response.data?.message || 'Archivo procesado exitosamente'
    };
  } catch (error) {
    const errorData = error.response?.data || {};
    return {
      success: false,
      error: errorData.message || errorData.error || 'Error al procesar el archivo',
      errores: errorData.errores || [],
      total_errores: errorData.total_errores || 0,
      fase: errorData.fase || null,
      data: errorData
    };
  }
};

/**
 * Obtener estado de proceso asíncrono de carga masiva de asignaciones
 * @param {Object} user - Usuario autenticado
 * @param {string} processId - ID del proceso
 * @returns {Promise}
 */
const getAsignacionesBulkStatus = async (user, processId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/extras/asignaciones/bulk/status/${processId}`;
    const response = await axios.get(endpoint, config);

    return {
      success: response.data?.success ?? true,
      processInfo: response.data?.process_info || null,
      data: response.data
    };
  } catch (error) {
    const errorData = error.response?.data || {};
    return {
      success: false,
      error: errorData.error || 'No fue posible obtener estado del proceso'
    };
  }
};

export {
  getExtras,
  getTiposExtra,
  createExtra,
  updateExtra,
  deleteExtra,
  validateExtraData,
  getAsignaciones,
  createAsignacion,
  updateAsignacion,
  bulkUpdateAsignacionesStatus,
  downloadPlantillaAsignacionesBulk,
  validateAsignacionesBulk,
  uploadAsignacionesBulk,
  getAsignacionesBulkStatus
};
